/**
 * Hook to fetch the authentication configuration from the backend.
 * Used by the login page to determine which login options to display.
 *
 * Requirements: 10.4, 10.5, 10.6
 */

import { useState, useEffect } from 'react';
import { apiClient } from '@/services/api-client';
import type { AuthConfig } from '@/types/auth-config';

interface UseAuthConfigResult {
  config: AuthConfig | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches the authentication configuration from GET /api/v1/auth/config.
 * On failure, falls back to `{ entraIdEnabled: false }` so the login page
 * always renders the email/password form as a safe default.
 */
export function useAuthConfig(): UseAuthConfigResult {
  const [config, setConfig] = useState<AuthConfig | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchConfig() {
      try {
        const response = await apiClient.get<AuthConfig>('/auth/config');
        if (!cancelled) {
          setConfig(response.data);
        }
      } catch {
        if (!cancelled) {
          setError('Failed to load authentication configuration');
          // Fallback: assume local-only auth
          setConfig({ entraIdEnabled: false });
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    fetchConfig();
    return () => {
      cancelled = true;
    };
  }, []);

  return { config, isLoading, error };
}
